import { distinctUntilChanged, filter, MonoTypeOperatorFunction, OperatorFunction } from 'rxjs';

/**
 * Filters out `null` and `undefined` values from an observable stream.
 *
 * This operator function can be applied to an Observable to ensure all emitted values are neither
 * `null` nor `undefined`. It is useful when you expect incomplete data that needs filtering.
 *
 * @example
 * const source$ = of(1, null, 2, undefined, 3);
 * const result$ = source$.pipe(filterNilValue());
 *
 * result$.subscribe(value => console.log(value));
 * // Logs: 1, 2, 3
 * @template T - The type of the observable values.
 * @returns An OperatorFunction that filters out `null` and `undefined` values.
 */
export function filterNilValue<T>(): OperatorFunction<T, NonNullable<T>> {
  return filter((value: T): value is NonNullable<T> => value !== null && value !== undefined);
}

/**
 * Returns an RxJS operator that filters out arrays containing null or undefined values.
 *
 * This helper operator is particularly useful in scenarios like using combineLatest,
 * where you need to ensure that all emitted values in the combined array are defined.
 *
 * @example
 * combineLatest([
 *   of(42),
 *   of(null),
 *   of('hello')
 * ]).pipe(
 *   filterNilArray()
 * ).subscribe();
 * // Logs: 42, 'hello'
 * @typeparam T Array type where each element is validated to be non-null and non-undefined.
 * @returns An operator function that filters out arrays with any null or undefined values.
 */
export function filterNilArray<T extends any[]>(): OperatorFunction<T, T> {
  return filter(values => !values.some(value => value === null || value === undefined));
}

/**
 * Emits only when an object's properties change (shallow comparison).
 *
 * @example
 * const source$ = of({ a: 1 }, { a: 1 }, { a: 2 });
 * source$.pipe(distinctUntilObjectChanged()).subscribe(console.log);
 * Logs: { a: 1 }, { a: 2 }
 */
export function distinctUntilObjectChanged<T extends Record<string, any>>(): MonoTypeOperatorFunction<T> {
  return distinctUntilChanged((prev, curr) => {
    if (typeof prev !== 'object' || typeof curr !== 'object' || prev === null || curr === null) {
      return prev === curr;
    }
    const prevKeys = Object.keys(prev);
    const currKeys = Object.keys(curr);
    if (prevKeys.length !== currKeys.length) return false;
    return !prevKeys.some(key => prev[key] !== curr[key]);
  });
}

/**
 * Emits new arrays only when a shallow comparison shows items have changed.
 * @example
 * // Emits only when the array content changes.
 * const numbers$ = of([
 *          [{ a:1 }, { a:2 }, { a:3 }],
 *          [{ a:1 }, { a:2 }, { a:3 }],
 *          [{ a:2 }, { a:2 }, { a:3 }],
 * ]);
 * numbers$.pipe(
 *   distinctUntilArrayItemChanged()
 * ).subscribe(console.log);
 * Logs: [{ a:1 }, { a:2 }, { a:3 }],
 *       [{ a:2 }, { a:2 }, { a:3 }],
 * @returns A MonoTypeOperatorFunction that filters out emissions with unchanged array items.
 */
export function distinctUntilArrayItemChanged<T>(): MonoTypeOperatorFunction<T[]> {
  return distinctUntilChanged((prev: T[], cur: T[]) => {
    if (prev === cur) return true;
    if (!isArray(prev) || !isArray(cur)) return false;
    if (isEmpty(prev) && isEmpty(cur)) return true;
    if (prev.length !== cur.length) return false;
    return !prev.some((item, index) => item !== cur[index]);
  });
}

function isArray<T>(value: any): value is T[] {
  return Array.isArray(value);
}

function isEmpty<T>(arr: T) {
  if (isArray(arr)) {
    return arr.length === 0;
  }
  return false;
}
